/**
 * @file: ReportViewSummaryContext.tsx
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Holds the stable `ReportViewSummaryStore` a list host owns. A host creates
 * it with `useCreateReportViewSummaryStore()` (which also refreshes the
 * visible cards whenever the screen regains focus — the read/view status is
 * changed by what happens on the task detail page) and provides it to the
 * cards below; cards read a single order's entry with `useReportViewSummary`.
 */

import { useIsFocused$ } from '@euler/functions/useIsFocused$';
import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks';
import { createContext, useContext, useEffect } from 'react';
import { filter, skip } from 'rxjs/operators';
import { ReportViewSummaryStore } from './ReportViewSummaryStore';

const ReportViewSummaryContext = createContext<ReportViewSummaryStore | null>(
  null,
);

export const ReportViewSummaryProvider = ReportViewSummaryContext.Provider;

export const useReportViewSummaryStore = () =>
  useContext(ReportViewSummaryContext);

/**
 * Creates the store for a list host. MUST be called inside a navigator screen
 * — the store refreshes the visible cards on every blur -> focus transition.
 */
export const useCreateReportViewSummaryStore = () => {
  const { orderService } = useServiceFactory();
  const store = useCreation(
    () => new ReportViewSummaryStore(orderService),
    [orderService],
    instance => instance.dispose(),
  );
  const isFocused$ = useIsFocused$();

  useEffect(() => {
    const sub = isFocused$
      .pipe(
        // skip the initial value — the first load is driven by visibility.
        skip(1),
        filter(Boolean),
      )
      .subscribe(() => store.refreshVisible());
    return () => sub.unsubscribe();
  }, [isFocused$, store]);

  return store;
};
